import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ErrorMessage } from '@l2u/web-shared';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '@l2u/web-shared';
import { SignInBrandLogo } from '@l2u/web-shared';
import { PoweredByFooter } from '@l2u/web-shared';
import { AppVersion } from '@l2u/web-shared';
import {
  SignInButton,
  SignInField,
  SignInHeading,
  SignInHelp,
  SignInLayout,
  SignInPasswordInput,
  SignInInput,
  SignInRule,
} from '@l2u/web-shared';
import { supportEmailOf } from '@l2u/shared/brandingDefaults';

/**
 * The admin front door, on the "Compliance Sign-in" canvas: a near-black
 * operations panel beside the form.
 *
 * The phone-only `TOUCH_CONTROL` overrides this file used to carry are gone,
 * not dropped: the sign-in controls are 50px and clear the 44px minimum at
 * every width by construction, and SignInControls.tsx keeps the 16px font floor
 * that stops iOS Safari zooming on focus (see DESK_PHONE_FONT there — same
 * reason, moved so all three audiences inherit it rather than one file).
 */
export function AdminLogin() {
  const { user, signInWithPassword } = useAdminAuth();
  const { branding } = useBranding();
  const supportEmail = supportEmailOf(branding);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  // Invite/recovery emails sometimes land here when Supabase's Site URL is
  // /login (e.g. the redirectTo we asked for wasn't on the allow-list).
  // Forward to the right credential-redeeming page based on the link's type.
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    if (!hash && !search) return;

    const fragment = hash ? new URLSearchParams(hash.replace(/^#/, '')) : null;
    const query = search ? new URLSearchParams(search) : null;
    const type = (fragment?.get('type') ?? query?.get('type') ?? '').toLowerCase();
    const target = type === 'recovery' ? '/reset-password' : '/accept-invite';

    if (fragment?.get('access_token') && fragment.get('refresh_token')) {
      navigate(`${target}${search}${hash}`, { replace: true });
      return;
    }
    if (query?.get('code') || query?.get('token_hash')) {
      navigate(`${target}${search}${hash}`, { replace: true });
    }
  }, [navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithPassword(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SignInLayout audience="admin">
      {/* The tenant's logo, or its name when none is uploaded. <h1>-less
          because SignInHeading below owns the page's <h1>. */}
      <SignInBrandLogo />

      {/* NOT "For SupplierClear staff", which the canvas proposed: this app
          serves the TENANT's compliance team. Platform staff sign in to
          apps/ops, on a different host entirely. */}
      <SignInHeading eyebrow="Admin portal" title="Sign in to the admin portal">
        Use the work email address and password for your compliance workspace.
      </SignInHeading>

      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <SignInField label="Email address" audience="admin">
          <SignInInput
            audience="admin"
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@company.com.au"
          />
        </SignInField>

        {/* The reset link moves into the label row, per the canvas. It is
            interactive content inside the <label>, so a click on it does not
            also forward activation to the password input — the same reason the
            SHOW/HIDE toggle can live there. */}
        <SignInField
          label="Password"
          audience="admin"
          action={
            <Link to="/forgot-password" className="text-[12.5px] font-semibold text-[#244232]">
              Forgot password?
            </Link>
          }
        >
          <SignInPasswordInput
            audience="admin"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </SignInField>

        <div role="alert" className="empty:hidden">
          <ErrorMessage error={error} />
        </div>

        <SignInButton
          audience="admin"
          type="submit"
          loading={submitting}
          disabled={!email.trim() || !password}
        >
          Sign in
        </SignInButton>
      </form>

      <SignInRule />

      <SignInHelp>
        {/* supportEmailOf, exactly as the portal's sign-in: the tenant's support
            address, else its from-address, else the platform's. The canvas
            hardcoded compliance@supplierclear.com, which is not an address this
            product owns or that any tenant would want their contractors writing to. */}
        <div>
          Still stuck? Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
        </div>
        <PoweredByFooter />
        <AppVersion />
      </SignInHelp>
    </SignInLayout>
  );
}
